import {fieldConditionHelper} from "@/form/helpers/field-conditionals.js";
import {businessIndustryChoices, rentMortgagePaymentStep} from "@/form/helpers/field-choices.js";
import {storageGetJson} from "@/utils/safe-storage.js";

/**
 * Business loan / merchant cash advance form. One schema serves both `business_loan` and `mca`
 * (steps-per-product.js); `app.product_name` tells them apart if a step ever needs to.
 *
 * Redesigned 2026-09 for the business lender integrations: purpose of funds, full address
 * (city/street reach CMI), business structure, ownership share, number of employees, business bank
 * account, date of birth, Ontario-only insolvency-consultation and housing questions, and partial
 * submits (unique_id). Fields on lms FieldLists/business_loan must stay unconditional -- see
 * tests/js/form/steps/business-loan.test.js ("lms complete lead contract").
 */
let steps = [
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__requested_amount_business__",
        fields: [
            {
                name: 'requested_amount',
                placeholder: '50000',
                prependInputSpan: '$',
                appendInputSpan: '.00',
                type: 'text',
                value: null,
                validationRules: 'required|numeric',
                fieldOuterClass: 'col-md-8 offset-md-2',
                hideIfHasValue: true,
                attrs: {
                    maxlength: 7
                },
            },
        ],
        hideButtonsSection: false,
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__purpose_of_funds__",
        fields: [
            {
                // Stored in the existing CMI/LMS `product_purpose` column. Values are Magical
                // Credit's enum verbatim; other partners receive the label.
                name: 'product_purpose',
                placeholder: '__select__',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    { "text": "__opt_business_purpose_everyday_operations__", "value": "everyday_operations" },
                    { "text": "__opt_business_purpose_purchase_inventory__", "value": "purchase_inventory" },
                    { "text": "__opt_business_purpose_purchase_new_equipment__", "value": "purchase_new_equipment" },
                    { "text": "__opt_business_purpose_expansion__", "value": "expansion" },
                    { "text": "__opt_business_purpose_renovations__", "value": "renovations" },
                    { "text": "__opt_business_purpose_sales_and_marketing__", "value": "sales_and_marketing" },
                    { "text": "__opt_business_purpose_hire_or_pay_staff__", "value": "hire_or_pay_staff" },
                    { "text": "__opt_business_purpose_invest_in_technology__", "value": "invest_in_technology" },
                    { "text": "__opt_business_purpose_start_a_business__", "value": "start_a_business" },
                    { "text": "__opt_business_purpose_other__", "value": "other" },
                ],
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        // Worded as "revenue", the term other lenders use; the wire key stays monthly_gross_sales.
        // The hint states the common $10,000/month lender minimum.
        title: '__average_monthly_revenue__',
        hint: '__average_monthly_revenue_hint__',
        fields: [
            {
                // The only sales question. A separate credit/debit card sales field (monthly_card_sales)
                // was dropped 2026-10-27: applicants typed the same amount into both, and buyers read
                // the two as adding up (5k + 5k = "10k revenue" against a 10k minimum). Buyers qualify
                // on this number alone; SharpShooter's total_monthly_card_sales now arrives blank.
                name: 'monthly_gross_sales',
                placeholder: '50000',
                prependInputSpan: '$',
                appendInputSpan: '.00',
                type: 'text',
                value: null,
                validationRules: 'required|numeric',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 7
                },
            },
        ],
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__business_start_date__",
        // Informational only: the form never blocks younger businesses; buyers filter on
        // months_in_business, which lms derives from this date.
        hint: '__business_start_date_hint__',
        fields: [
            {
                // Month and year only (DatePicker's bizStart config). Stored as the 1st of the
                // picked month, YYYY-MM-01, so the backend date rules are unchanged.
                name: 'business_start_date',
                placeholder: '__select_month__',
                type: 'date',
                value: null,
                // monthYear instead of date: same check, but its message speaks about a month and
                // year, and it stays quiet on an empty value so only "required" shows.
                validationRules: 'required|monthYear',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 10
                },
            },
        ],
    },
    {
        // Same step as personal_loan.js. Writes the nested `app.address` object, which is the only
        // source the CMI mapper reads `city` / street from (flat `postal_code` is not enough).
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__address__",
        notice: (app) => {
            if (!app._isPrepop) return false;
            if (app.prepop_address) return '__prepop_confirm_address__';
            const country = (app.projectBrandCountry || 'ca').toLowerCase();
            const stored = storageGetJson(`comparehub-user-address-${country}`);
            if (stored?.addressData && new Date(stored.expiry) > new Date()) return '__prepop_confirm_address__';
            return false;
        },
        fields: [
            {
                name: 'address',
                placeholder: '',
                type: 'fulladdress',
                fieldOuterClass: 'col-md-8 offset-md-2',
                value: null,
            },
        ],
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__in_credit_situation__",
        fields: [
            {
                name: 'in_credit_situation',
                placeholder: '__select__',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    {"text": "__opt_in_credit_situation_good__", "value": "good"},
                    {"text": "__opt_in_credit_situation_fair__", "value": "fair"},
                    {"text": "__opt_in_credit_situation_low__", "value": "low"},
                    {"text": "__opt_in_credit_situation_cp__", "value":"in_consumer_proposal"},
                    {"text": "__opt_in_credit_situation_bk__", "value":"in_bankruptcy"},
                    {"text": "__opt_in_credit_situation_unknown__", "value": "unknown"},
                ],
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
    },
    {
        // Ontario-only lender filter, asked only with fair/low/unknown credit: "good" makes a trustee
        // unlikely and bankruptcy / consumer proposal imply one. Needs the address step before it.
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__insolvency_consultation__",
        fields: [
            {
                name: 'insolvency_consultation',
                placeholder: '',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    { "text":"__opt_insolvency_consultation_none__", "value":"none" },
                    { "text":"__opt_insolvency_consultation_within_6_months__", "value":"within_6_months" },
                    { "text":"__opt_insolvency_consultation_over_6_months__", "value":"over_6_months" },
                ],
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
        hideIfCondition: fieldConditionHelper.skipTrusteeQuestion,
    },
    // canadian_status is deliberately not asked here (unlike the consumer forms): no business partner
    // reads it and the startup mailer that did is disabled in lms.
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: '__company_information__',
        hint: '__business_structure_hint__',
        fields: [
            {
                label: '__company_name__',
                name: 'company_name',
                type: 'text',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 100
                },
            },
            {
                label: '__business_structure__',
                name: 'business_structure',
                placeholder: '',
                type: 'select',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    {"value":"corporation", "text":"__opt_business_structure_corporation__"},
                    {"value":"sole_proprietorship", "text":"__opt_business_structure_sole_proprietorship__"},
                    {"value":"general_partnership", "text":"__opt_business_structure_general_partnership__"},
                    {"value":"limited_partnership", "text":"__opt_business_structure_limited_partnership__"},
                    {"value":"non_profit", "text":"__opt_business_structure_non_profit__"},
                    {"value":"other", "text":"__opt_business_structure_other__"},
                ],
                hideIfHasValue: false,
            },
            {
                label: '__industry__',
                name: 'business_type',
                placeholder: '',
                type: 'select',
                value: null,
                // inChoices: a prepopped code the list no longer offers (e.g. LAW_FIRM) must be
                // replaced by the applicant, not silently submitted behind a blank select.
                validationRules: 'required|inChoices',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: businessIndustryChoices(),
                hideIfHasValue: false,
            },
            {
                // The value sent is the lower bound of the bucket so LMS stores a plain integer.
                label: '__number_of_employees__',
                name: 'number_of_employees',
                placeholder: '',
                type: 'select',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    {"value":"1", "text":"__opt_number_of_employees_1__"},
                    {"value":"2", "text":"__opt_number_of_employees_2_5__"},
                    {"value":"6", "text":"__opt_number_of_employees_6_10__"},
                    {"value":"11", "text":"__opt_number_of_employees_11_50__"},
                    {"value":"51", "text":"__opt_number_of_employees_51_plus__"},
                ],
                hideIfHasValue: false,
            },
        ],
    },
    // One radio per step: a radio-button click always advances the step (LcField
    // handleInputFromRadioButton), so several radios on one step would lose the other answers.
    {
        // Stored as the lower bound of the bucket. Not asked for sole proprietorships (lms derives
        // 100) or non-profits (stays null); partnerships cannot pick "only owner".
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: '__ownership_percentage__',
        fields: [
            {
                name: 'ownership_percentage',
                placeholder: '',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: (app) => {
                    const choices = [
                        { "text":"__opt_ownership_percentage_100__", "value":"100" },
                        { "text":"__opt_ownership_percentage_51__", "value":"51" },
                        { "text":"__opt_ownership_percentage_50__", "value":"50" },
                        { "text":"__opt_ownership_percentage_1__", "value":"1" },
                    ];
                    return fieldConditionHelper.isPartnership(app)
                        ? choices.filter((choice) => choice.value !== "100")
                        : choices;
                },
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
        hideIfCondition: fieldConditionHelper.ownershipImplied,
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: '__has_business_bank_account__',
        fields: [
            {
                name: 'has_business_bank_account',
                placeholder: '',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    { "text":"__opt_yes__", "value":"yes" },
                    { "text":"__opt_no__", "value":"no" },
                ],
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__how_to_contact__",
        fields: [
            {
                label: '__first_name__',
                name: 'first_name',
                type: 'text',
                value: null,
                validationRules: 'required|name',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 50
                },
                hideIfHasValue: false,
            },
            {
                label: '__last_name__',
                name: 'last_name',
                type: 'text',
                value: null,
                validationRules: 'required|name',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 50
                },
                hideIfHasValue: false,
            },
            {
                label: '__email_address__',
                name: 'email',
                type: 'text',
                value: null,
                validationRules: 'required|email',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 50
                }
            },
            {
                // Fallback only: the CMI mapper prefers address.province, so this is hidden
                // as soon as the address step has supplied the province.
                label: '__province__',
                name: 'state',
                type: 'select',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    {"value":"AB", "text":"ALBERTA"},
                    {"value":"BC", "text":"BRITISH COLUMBIA"},
                    {"value":"MB", "text":"MANITOBA"},
                    {"value":"NB", "text":"NEW BRUNSWICK"},
                    {"value":"NL", "text":"NEWFOUNDLAND AND LABRADOR"},
                    {"value":"NS", "text":"NOVA SCOTIA"},
                    {"value":"ON", "text":"ONTARIO"},
                    {"value":"PE", "text":"PRINCE EDWARD ISLAND"},
                    {"value":"QC", "text":"QUEBEC"},
                    {"value":"SK", "text":"SASKATCHEWAN"},
                    {"value":"NT", "text":"NORTHWEST TERRITORIES"},
                    {"value":"NU", "text":"NUNAVUT"},
                    {"value":"YT", "text":"YUKON"},
                ],
                hideIfHasValue: false,
                hideIfCondition: (app) => !!app.address?.province,
            },
            {
                label: '__phone_number__',
                name: 'phone_number',
                type: 'text',
                value: null,
                validationRules: 'required|canadianPhone',
                fieldOuterClass: 'col-md-8 offset-md-2',
                attrs: {
                    maxlength: 10
                },
                hideIfHasValue: false,
                textBottom: '__phone_number_sms_results__'
            },
        ],
        textBottomBelowActionButtons: "__credit_history_authorization_1__"
    },
    {
        // Partial submits switch on here and fire on the way to the steps below, so the lead gets
        // its unique_id even when this step is hidden (same layout as personal_loan.js).
        beforeCreate: 'enablePartialRequestPerStep',
        beforeNextStep: function() {},
        title: "__sms_verification__",
        fields: [
            {
                name: 'sms_verification',
                type: 'smsverification',
                value: null,
                hideSubtitles: true,
            },
        ],
        hideButtonsSection: true,
        nextButtonText: '__skip__',
        nextButtonClass: 'btn-secondary',
        hideIfCondition: (app) => {
            return !app.forceSmsVerification;
        }
    },
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__dob__",
        fields: [
            {
                name: 'dob',
                placeholder: null,
                type: 'date',
                value: null,
                validationRules: 'required|dob',
                fieldOuterClass: 'col-md-6 offset-md-3',
                attrs: {
                    maxlength: 10
                },
                hideIfHasValue: true,
            },
        ],
    },
    // Ontario only. The planned Magical Business integration will derive the lender's housing status
    // from these two answers (own_home x whether the payment is zero), so 0 is a real answer.
    {
        beforeCreate: function() {},
        beforeNextStep: function() {},
        title: "__own_home__",
        fields: [
            {
                name: 'own_home',
                placeholder: '',
                type: 'radio',
                value: null,
                validationRules: 'required',
                fieldOuterClass: 'col-md-8 offset-md-2',
                choices: [
                    { "text":"__opt_yes__", "value":"yes" },
                    { "text":"__opt_no__", "value":"no" },
                ],
                hideIfHasValue: true,
            },
        ],
        hideButtonsSection: true,
        hideIfCondition: fieldConditionHelper.notOntario,
    },
    // Owners are asked about their mortgage, renters about their rent; bands and stored values live in
    // the shared helper so every product asks this the same way.
    rentMortgagePaymentStep({ hideIfCondition: fieldConditionHelper.notOntario }),
];

export default steps;
